import { formatChipsShort } from '@polf/shared/locale'
import { useState } from 'react'

import type { FlightPlayerScoresFragment } from '../../../__generated__/hooks'
import { getHoleStrokes, getStablefordPoints } from '../../../util/scoring'
import { termFor, TONE_STYLES } from '../../../util/term'
import Avatar from './Avatar'
import HighScoreSheet from './HighScoreSheet'
import ScoreButtons from './ScoreButtons'

/**
 * One player/team row in the score-entry grid. Header (avatar + name +
 * HCP/stroke meta), 7-cell score grid, footer (hole result + accumulated
 * round totals + optional 'Brukt av laget' best-ball pill).
 *
 * `usedByTeam` paints the gold ring + footer pill in best-ball when this
 * card's score is the team's chosen "best" on the current hole.
 */
const PlayerScoreCard = ({
  entry,
  holeNumber,
  isYou,
  showChips,
  isNet,
  usedByTeam,
  onSetScore,
  saving,
}: {
  entry: FlightPlayerScoresFragment
  holeNumber: number
  isYou: boolean
  showChips: boolean
  /** NET_STROKES ranking — footer total shows net strokes instead of pts. */
  isNet: boolean
  usedByTeam: boolean
  onSetScore: (scoreId: string, gross: number) => void
  saving: boolean
}) => {
  const [overflowOpen, setOverflowOpen] = useState(false)
  const holeScore = (entry.scores ?? []).find(
    (s) => s.hole.number === holeNumber
  )
  if (!holeScore) {
    return (
      <div className="bg-paper rounded-lg p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Avatar name={entry.name} isYou={isYou} />
          <div className="flex-1 text-sm font-semibold">{entry.name}</div>
        </div>
        <p className="text-xs text-ink-500 mt-2">
          Ingen score-rad funnet for dette hullet
        </p>
      </div>
    )
  }
  const { hole, score, chips } = holeScore
  const selected = score ?? null
  const term = selected != null ? termFor(selected, hole.par) : null
  const tone = term ? TONE_STYLES[term.tone] : null
  const acc = entry.accumulated
  // Per-hole strokes + stableford recomputed from the top-level
  // `entry.strokes` (which IS correctly resolved). The score-level
  // getters on the API return 0 for Scramble team scores because the
  // score's `.team` relation isn't loaded with polfEvent — so we
  // derive locally from the team's total and the hole's index. Same
  // algorithm as the server, kept in sync via `util/scoring.ts`.
  const strokes =
    entry.strokes > 0 ? getHoleStrokes(entry.strokes, hole.index) : 0
  const stablefordPoints =
    selected != null ? getStablefordPoints(hole.par, selected, strokes) : 0
  const dotCount = Math.min(strokes, 4)

  return (
    <div
      className={`relative overflow-hidden bg-paper rounded-lg p-3 shadow-sm ${
        isYou ? 'bg-green-50' : ''
      } ${usedByTeam ? 'ring-2 ring-gold' : ''}`}
    >
      {/* "You" left-border accent per design.md §8.1 — parent uses
          overflow-hidden so the strip is clipped to the rounded corners. */}
      {isYou ? (
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-green-700"
        />
      ) : null}

      <div className="flex items-center gap-3 mb-2">
        <Avatar name={entry.name} isYou={isYou} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-semibold truncate ${
                isYou ? 'text-green-800' : 'text-ink-900'
              }`}
            >
              {entry.name}
            </span>
            {isYou ? (
              <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                Du
              </span>
            ) : null}
          </div>
          <div className="text-[11px] flex items-center justify-between gap-2 mt-0.5">
            <span className="text-ink-500 font-mono num">
              HCP {entry.hcp ?? '—'}
            </span>
            {strokes > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex items-center gap-0.5">
                  {Array.from({ length: dotCount }).map((_, i) => (
                    <span
                      // Decorative count-dots — no identity beyond index.
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-flag"
                    />
                  ))}
                </span>
                <span className="font-semibold text-ink-700">
                  +{strokes} slag fra hcp
                </span>
              </span>
            ) : (
              <span className="text-ink-500">ingen slag fra hcp</span>
            )}
          </div>
        </div>
      </div>

      <ScoreButtons
        par={hole.par}
        selected={selected}
        disabled={saving}
        // Toggle behaviour: tapping the currently-selected cell sends
        // 0, which the API treats as a clear (see service.updateScore
        // — `!!score ? score : null`).
        onPick={(g) => onSetScore(holeScore.id, g === selected ? 0 : g)}
        onOverflow={() => setOverflowOpen(true)}
      />

      <div className="mt-2.5 flex items-center justify-between text-xs gap-2 flex-wrap">
        {usedByTeam ? (
          <span
            className="inline-flex items-center gap-1 bg-gold/15 text-gold px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            aria-label="Brukt av laget"
          >
            🏆 Brukt av laget
          </span>
        ) : null}
        <div className="min-w-0">
          {selected == null ? (
            <span className="text-ink-500">Ingen score registrert</span>
          ) : (
            <span className={`font-semibold ${tone?.fg ?? ''}`}>
              {term?.label}
              {strokes > 0 ? (
                <span className="text-ink-500 font-mono num ml-2">
                  netto {selected - strokes}
                </span>
              ) : null}
              <span className="text-ink-500 ml-2 num">
                · {stablefordPoints} pts
              </span>
              {showChips && chips > 0 ? (
                <span className="font-mono num font-bold text-gold ml-2">
                  +{formatChipsShort(chips)}
                </span>
              ) : null}
            </span>
          )}
        </div>
        <div className="text-[11px] text-ink-500 font-mono num shrink-0">
          Totalt{' '}
          {showChips ? (
            <>
              <span className="text-ink-900 font-bold">
                {formatChipsShort(acc.chips)}
              </span>
              <span className="text-ink-300"> · </span>
            </>
          ) : null}
          {/* Primary running stat tracks the event ranking:
              - STABLEFORD: cumulative pts ("32 pts")
              - NET_STROKES: to-par over played holes ("−1" / "E" / "+5"),
                red when under par. acc.score is sum of (netScore - par)
                already — exactly what golfers expect to see. */}
          {isNet ? (
            (() => {
              const toPar = acc.score
              const display =
                toPar === 0 ? 'E' : toPar > 0 ? `+${toPar}` : `${toPar}`
              const color = toPar < 0 ? 'text-flag-dark' : 'text-ink-900'
              return <span className={`font-bold ${color}`}>{display}</span>
            })()
          ) : (
            <>
              <span className="text-ink-900 font-bold">
                {acc.stablefordPoints}
              </span>
              <span className="text-ink-500"> pts</span>
            </>
          )}
        </div>
      </div>

      <HighScoreSheet
        open={overflowOpen}
        par={hole.par}
        initial={selected != null && selected > hole.par + 3 ? selected : null}
        onClose={() => setOverflowOpen(false)}
        onSave={(g) => {
          onSetScore(holeScore.id, g)
          setOverflowOpen(false)
        }}
      />
    </div>
  )
}

export default PlayerScoreCard
